import { useQuery } from '@tanstack/react-query'
import type { ContentTextDTO, ContentVersionDTO } from '@today/types'
import { EmptyState } from '@today/ui-components'
import {
	AddLanguageDialogView,
	ApprovalTimelineView,
	ApproveDialogView,
	DiffView,
	LanguageTabsView,
	LifecycleBarView,
	PublishDialogView,
	ReasonDialogView,
	TextHeaderView,
	VersionsPanelView,
} from '@today/ui-domain'
import { useParams } from 'react-router-dom'
import { contentErrorStatus } from '@/hooks/content/content-ui'
import { contentKeys, useContentT, useTextVersions } from '@/hooks/content/useContentQueries'
import { useTextEditor } from '@/hooks/content/useTextEditor'
import { ForbiddenPage } from '@/pages/errors/Forbidden'
import { contentService } from '@/services/content.service'

function LoadedText({ text, versions }: { text: ContentTextDTO; versions: ContentVersionDTO[] }) {
	const editor = useTextEditor(text, versions)
	return (
		<div className="flex flex-col gap-8">
			<TextHeaderView {...editor.header} />
			<VersionsPanelView {...editor.versions} />
			{editor.diff ? <DiffView {...editor.diff} /> : null}
			<LanguageTabsView {...editor.tabs} lifecycle={<LifecycleBarView {...editor.lifecycle} />} />
			{editor.timeline ? <ApprovalTimelineView {...editor.timeline} /> : null}
			<ApproveDialogView {...editor.approve} />
			<ReasonDialogView {...editor.reason} />
			<PublishDialogView {...editor.publish} />
			<AddLanguageDialogView {...editor.addLanguage} />
		</div>
	)
}

/** `/content/texts/:id` — the text editor (TO-420 content-admin-ui REQ-003…REQ-007). */
export function TextEditorPage() {
	const { t } = useContentT()
	const { id = '' } = useParams()
	const text = useQuery({ queryKey: contentKeys.text(id), queryFn: () => contentService.getText(id) })
	const versions = useTextVersions(id)
	const error = text.error ?? versions.error
	const status = contentErrorStatus(error)
	if (status === 403) return <ForbiddenPage />
	if (status === 404) return <EmptyState title={t('notFound')} />
	if (error)
		return (
			<EmptyState
				title={t('errors.generic')}
				action={{
					label: t('actions.retry'),
					onPress: () => {
						void text.refetch()
						void versions.refetch()
					},
				}}
			/>
		)
	return text.data && versions.data ? <LoadedText key={id} text={text.data} versions={versions.data} /> : null
}
